import { ValidationRule } from 'react-hook-form';
import { IOption } from './Form';

export interface IOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  customerId: number;
  customerUnit: IEntityReference | null;
  customerContact: string;
  customerContactPhone: string;
  customerContactEmail: string;
  orderType: number;
  orderStatus: number;
  createdDate: string;
  updatedDate: string;
  assignedUser: string;
  responsible: string;
  requestedPersonalNumber: string;
  requestedName: string;
  requestedEmail: string;
  requestedPhone: string;
  requestedHomeAddress: string;
  smsConsent: boolean;
  requestedBriefDesc: string;
  requestedAdditionalInformation: string;
  requestedNumberOfAttendees: number;
  healthCenter: IEntityReference | null;
  requestedDate: string | null;
  digitalDelivery: boolean;
  bookingDate: string | null;
  bookingDetails: string;
  feedbackPerformed: number;
  feedbackRecipient: boolean;
  feedbackContactDetails: string;
  attachments: ViewAttachment[];
  additionalHealthCenters: IEntityReference[];
  customerUnitId: number;
}

export interface IOrderModel {
  id: number;
  orderNumber: string;
  customerName: string;
  customerId: number;
  customerUnit: IEntityReference;
  customerContact: string;
  customerContactPhone: string;
  customerContactEmail: string;
  orderType: number;
  orderStatus: number;
  createdDate: string;
  updatedDate: string;
  assignedUser: string;
  responsible: string;
  requestedPersonalNumber: string;
  requestedName: string;
  requestedEmail: string;
  requestedPhone: string;
  requestedHomeAddress: string;
  smsConsent: boolean;
  requestedBriefDesc: string;
  requestedAdditionalInformation: string;
  requestedNumberOfAttendees: number;
  healthCenter: IEntityReference;
  requestedDate: string | null;
  requestedDeliveryDateSpecification: IEntityReference;
  digitalDelivery: string;
  bookingDate: string | null;
  bookingDetails: string;
  feedbackPerformed: number;
  feedbackRecipient?: IEntityReference;
  feedbackContactDetails: string;
  attachments: File[];
  additionalHealthCenters: number[];
  isBookedDateEmpty: boolean;
  customerUnitId: number;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface IOrderRequest {
  customerContact: string;
  customerContactPhone: string;
  customerContactEmail: string;
  customerUnitId: number;
  requestedName: string;
  requestedPersonalNumber: string;
  requestedPhone: string | null;
  requestedEmail: string | null;
  requestedHomeAddress: string | null;
  requestedBriefDesc: string;
  requestedAdditionalInformation: string | null;
  requestedDate: string | null;
  digitalDelivery: boolean;
  smsConsent: boolean;
  feedbackPerformed: number;
  feedbackRecipient: boolean;
  feedbackContactDetails: string | null;
  healthCenterId: number;
  requestedNumberOfAttendees: number;
  orderType: number;
  additionalHealthCenters: number[];
}

export interface IOrderPage {
  items: IOrderReference[];
  pageInfo: Pagination;
}

export interface IOrderReference {
  id: number;
  customerContact: string;
  createdDate: Date;
  orderNumber: string;
  orderStatus: number;
  orderType: number;
  customerUnitName: string;
  employeeName: string;
  customerUnitId: number;
}

export interface SaveOrderResponse {
  id: number;
  orderNumber: string;
}

export interface IEntityReference {
  id: number | string;
  name: string;
}

export interface IOrderFileReference {
  displayName: string;
  uri: string;
}

export interface ViewAttachment {
  id: number;
  title: string;
}

export enum OrderType {
  Individual = 'Individual',
  Education = 'Education',
  Group = 'Group',
}

export enum OrderStatus {
  ForDelivery = 'For Delivery',
  Booked = 'Booked',
  Cancelled = 'Canceled',
  PartnerBooked = 'PartnerBooked',
  Planning = 'Planning',
  WaitingForCustomer = 'WaitingForCustomer',
}

export enum FeedbackPerformed {
  No = 'orders.OPTION_FEEDBACK_METHOD_NO',
  Yes = 'orders.OPTION_FEEDBACK_METHOD_YES',
}

export interface OrderState {
  aEHierarchyOptions: IOption<string>[];
  aeHierarchyLoadState: string;
  hierarchyOptions: IOption<string>[];
  hierarchyLoadState: string;
  showResultModal: boolean;
  submissionState: string;
  orderLoadState: string;
  feedbackOptions: IOption<number>[];
  readOnlyOrder?: IOrder;
}

export interface OrderDefinition {
  type: string;
  fieldName: FieldName;
  label: string;
  placeholder?: string;
  description?: string;
  rules?: InputRules;
  class?: string;
}

export interface OrderDefinitions {
  orderInfo: OrderDefinition[];
  customerInfoFirst: OrderDefinition[];
  employeeInfo: OrderDefinition[];
  groupInfo: OrderDefinition[];
  educationInfo: OrderDefinition[];
  descriptionInfo: OrderDefinition[];
}

interface InputRules {
  required: boolean;
  pattern?: ValidationRule<RegExp>;
  min?: number;
  max?: number;
}

type FieldName =
  | 'id'
  | 'orderNumber'
  | 'customerName'
  | 'customerUnit'
  | 'customerContact'
  | 'customerContactPhone'
  | 'customerContactEmail'
  | 'orderType'
  | 'orderStatus'
  | 'createdDate'
  | 'updatedDate'
  | 'assignedUser'
  | 'responsible'
  | 'requestedPersonalNumber'
  | 'requestedName'
  | 'requestedEmail'
  | 'requestedPhone'
  | 'requestedHomeAddress'
  | 'smsConsent'
  | 'requestedBriefDesc'
  | 'requestedAdditionalInformation'
  | 'requestedNumberOfAttendees'
  | 'healthCenter'
  | 'requestedDate'
  | 'digitalDelivery'
  | 'bookingDate'
  | 'bookingDetails'
  | 'feedbackPerformed'
  | 'feedbackRecipient'
  | 'feedbackContactDetails'
  | 'attachments'
  | 'additionalHealthCenters';
